/*
 * What each role may open, by address.
 *
 * The sidebar has always decided what a role is SHOWN. It never decided what a
 * role can REACH: four roles had an allow-list, the Owner and the AI Champion
 * had none at all, and every allow-list carried a tail of "no longer nav, but
 * still reachable by URL" addresses. So an Owner who typed /aai/departments got
 * the Trainer's roster screen, and nothing said no.
 *
 * This is the other half: one entry per address a role's own UI actually offers
 * it, and nothing else. Typing anything outside the list returns the person to
 * their home. The rule the lists are built from is the product rule — if a role
 * cannot get somewhere by clicking, it cannot get there by typing.
 *
 * Two kinds of entry:
 *   '/projects'    exactly that path. Query strings are free, so a screen whose
 *                  tabs are ?tab= needs one entry, not one per tab.
 *   '/problems/*'  that path and anything under it, for the screens that carry
 *                  a record id in the address.
 *
 * '/aai' is deliberately exact everywhere. The AI Adoption hub renders its tabs
 * in place under ?tab=, so its children (/aai/departments, /aai/curriculum,
 * /aai/cohorts) are separate screens belonging to separate roles, and a prefix
 * match on '/aai' would hand every one of them to everybody.
 *
 * A redirect still needs its own entry: the guard runs before the route does,
 * so /results has to be reachable for /results to bounce to Home.
 */

/** Addresses every signed-in workspace member reaches: their home, the account
 *  panel, the bell, and the two Orbit entry points in the chrome. */
const SHARED = [
  '/dashboard',
  '/settings',
  '/notifications',
  '/orbit/request',
  '/orbit/deck',
];

/** Onboarding, readiness and training: every role that answers Orbit's own
 *  questions about themselves, which is everyone except the Expert. */
const OWN_GROWTH = [
  '/onboarding',
  '/my-readiness',
  '/my-training',
  '/my-sessions',
];

/** The reference shelf behind the AI Adoption hub. */
const REFERENCE = [
  '/help-community',
  '/whats-allowed',
];

export const ROLE_ROUTES: Record<string, string[]> = {
  OWNER: [
    ...SHARED, ...REFERENCE,
    '/projects/*', '/aai', '/company-context',
    // Every file the company's work holds. The Owner's alone for now: it spans
    // every project room in the company, which is the only scope that is.
    '/documents',
    // Reached from Home: the recommendation history and the use-case registry.
    '/portfolio', '/registry', '/aai/registry', '/vendors', '/aai/vendors',
    // Folded surfaces. Each one redirects; the guard has to let it through first.
    '/controls', '/results', '/room', '/client', '/adoption', '/adoption/*', '/framework',
    '/controls/ai',
    // The plan itself. `/client` above does not cover these: the match is exact
    // unless an entry says otherwise, so listing the parent let Manage plan
    // navigate and then bounced the Owner straight back to their dashboard.
    // Only the Owner holds `manage_client_billing`, so only the Owner has them.
    '/client/billing', '/client/payments', '/billing/invoices', '/billing/usage/*', '/usage', '/usage/*', '/billing/project-budgets/*',
    // Notification targets and record links.
    '/problems/*', '/requests', '/reports', '/aai/skills',
    // Company records the Owner owns: people, the company facts, the store.
    '/team', '/my-business', '/knowledge',
    // The account menu. The marketplace entry is a prefix: the chatbot detail
    // (/marketplace/chatbot and its preparing step) sits under it, and an exact
    // match let the card open and then bounced the Owner back to the dashboard.
    '/marketplace/*', '/my-tools',
    // The builder itself. The Owner holds `aai_build_skills`, so the permission
    // gate behind this address lets them in; leaving it off the role's list
    // meant the role guard bounced them to the dashboard before that gate ever
    // ran, and the row was retired from the sidebar in the same round. The two
    // together left an address the Owner is allowed to open and cannot.
    '/skill-builder', '/skill-builder/workflow',
    // A provider company's Owner adds and opens its clients here. The pages
    // themselves send anyone else home.
    '/clients/*',
    '/scope',
    '/connectors', '/billing',
  ],

  // Runs the programme; does not hold the company's record. Company Context is
  // off for now and can be granted back by adding the one entry here.
  AI_CHAMPION: [
    ...SHARED, ...REFERENCE, ...OWN_GROWTH,
    '/projects/*', '/aai', '/portfolio',
    '/registry', '/aai/registry', '/vendors', '/aai/vendors',
    '/results', '/room', '/client', '/adoption', '/adoption/*',
    '/problems/*', '/requests', '/reports', '/aai/skills', '/skill-builder', '/skill-builder/workflow',
    '/org-chart', '/aai/curriculum', '/knowledge',
    '/team', '/marketplace/*', '/my-tools', '/tools',
    '/scope',
  ],

  // A department operating role. Everything company-wide is off: no portfolio,
  // no registry, no org chart, no other department's screens.
  DEPARTMENT_HEAD: [
    ...SHARED, ...REFERENCE, ...OWN_GROWTH,
    '/projects/*', '/aai', '/company-context',
    '/results', '/documents',
    '/requests', '/aai/skills', '/my-tools', '/tools',
    '/scope',
  ],

  // The narrowest workspace role: their own work, their own growth, and the
  // questions Orbit hands them.
  //
  // Company Context is not theirs. It was given to them for one round, to fix a
  // test that expected an employee to read the questions coming their way, and
  // taken back: the screen is the company's own record, and a company only has
  // one once its owner has set it up, departments and all. An employee reaching
  // it before that reads a page about a company that has not been described
  // yet. Their own questions reach them on Home, where their work is.
  AI_PARTICIPANT: [
    ...SHARED, ...REFERENCE, ...OWN_GROWTH,
    '/my-projects', '/ai-adoption', '/quick-wins', '/results', '/documents',
    '/requests', '/aai/skills', '/skill-builder', '/skill-builder/workflow',
    '/my-tools', '/tools', '/my-productivity',
    '/scope',
  ],

  // External delivery. The cockpit, and nothing about the company's own work.
  TRAINER: [
    ...SHARED, ...OWN_GROWTH, ...REFERENCE,
    '/aai', '/aai/curriculum', '/aai/templates', '/aai/delivery', '/aai/cohorts',
    '/aai/departments', '/aai/skills', '/org-chart',
  ],

  // The engineer gate: the review queue and the work assigned by name.
  EXPERT: [
    ...SHARED, ...REFERENCE,
    '/aai', '/aai/prd-reviews', '/aai/registry', '/registry',
    '/my-work', '/requests', '/problems/*', '/projects/*',
    '/onboarding', '/my-readiness',
  ],

  // Room-only roles: they answer questions about the company and nothing else.
  // Their nav is Home, the Ask Orbit card and Company Context, and this is the
  // whole of it — no Projects, no My Business, no My Tools, none of which they
  // hold a grant for.
  EXECUTIVE: [...SHARED, ...OWN_GROWTH, '/company-context'],
  // A team lead is assignable too, so work given to them by name has a home.
  TEAM_LEAD: [...SHARED, ...OWN_GROWTH, '/company-context', '/my-work'],
};

/** Where a role lands when it asks for something outside its list. */
export const ROLE_HOME: Record<string, string> = {
  EXPERT: '/aai',
  TRAINER: '/dashboard',
};

export const homeFor = (role: string | null): string => {
  if (!role) return '/dashboard';
  return ROLE_HOME[role] || '/dashboard';
};

/**
 * `granted` are addresses this person reaches through something other than
 * their role: the expert flag, or a grant their department carries. The
 * sidebar already offers them those rows, and a list keyed on role alone
 * cannot see either, so the caller passes what it is showing.
 */
export const mayOpen = (role: string | null, path: string, granted: string[] = []): boolean => {
  if (!role) return true;
  if (granted.includes(path)) return true;
  const allowed = ROLE_ROUTES[role];
  // A workspace role with no list is not restricted. That is the plain client
  // and anything added later: a role this file has not been taught is given the
  // benefit of the doubt rather than locked out of its own product.
  if (!allowed) return true;
  for (const entry of allowed) {
    if (entry.endsWith('/*')) {
      const base = entry.slice(0, -2);
      if (path === base) return true;
      if (path.startsWith(`${base}/`)) return true;
      continue;
    }
    if (path === entry) return true;
  }
  return false;
};
